import { useState } from "react";
import { Sparkles, Loader2, Check, RotateCcw } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ASSIST_ACTIONS } from "@/lib/cvUtils";

// Reusable AI Assistant for any CV text field. Never overwrites silently — the user
// reviews the suggestion and taps "Use this" to apply it.
export default function AiAssist({ text, context = "", onResult, actions = ASSIST_ACTIONS, testid = "ai-assist", label = "AI Assistant" }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (action) => {
    if (!(text || "").trim()) { toast.error("Write something first, then let AI refine it."); return; }
    setLoading(true); setResult(null);
    try {
      const r = await api.cvAssist(action, text, context);
      setResult(r.result || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI Assistant is unavailable right now.");
    } finally { setLoading(false); }
  };

  const apply = () => { onResult?.(result); setOpen(false); setResult(null); toast.success("Applied AI suggestion"); };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setResult(null); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`${testid}-btn`}
          className="inline-flex items-center gap-1 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-300 transition-colors hover:bg-emerald-400/20">
          <Sparkles size={12} /> {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 border-white/10 bg-[#111214] p-3 text-white" data-testid={`${testid}-panel`}>
        {!result && !loading && (
          <div className="grid grid-cols-2 gap-1.5">
            {actions.map((a) => (
              <button key={a.key} data-testid={`${testid}-action-${a.key}`} onClick={() => run(a.key)}
                className="rounded-md border border-white/10 bg-white/5 px-2 py-1.5 text-left text-[12px] font-medium text-[#d4d4d8] transition-colors hover:border-emerald-400/40 hover:text-white">
                {a.label}
              </button>
            ))}
          </div>
        )}
        {loading && <div className="flex items-center gap-2 py-6 text-sm text-[#a1a1aa]"><Loader2 size={16} className="animate-spin text-emerald-300" /> Thinking…</div>}
        {result != null && !loading && (
          <div className="space-y-2">
            <div data-testid={`${testid}-result`} className="max-h-52 overflow-auto whitespace-pre-wrap rounded-md border border-white/10 bg-black/40 p-2 text-[12.5px] leading-relaxed text-[#e4e4e7]">{result}</div>
            <div className="flex gap-2">
              <button data-testid={`${testid}-apply`} onClick={apply} className="inline-flex flex-1 items-center justify-center gap-1 rounded-md bg-emerald-500 px-2 py-1.5 text-[12px] font-bold text-black hover:bg-emerald-400"><Check size={13} /> Use this</button>
              <button data-testid={`${testid}-retry`} onClick={() => setResult(null)} className="inline-flex items-center justify-center gap-1 rounded-md border border-white/15 px-2 py-1.5 text-[12px] font-semibold text-white hover:border-white/30"><RotateCcw size={12} /> Back</button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
